import React, { Suspense, useEffect } from "react";
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import CssBaseline from '@mui/material/CssBaseline';
import * as routes from './config/routePaths';
import theme from "./config/theme";
import bugsnagClient from "./services/bugsnag/bugsnag";
import Loading from "./components/Template/Loading/Loading";
import Sidebar from "./components/pages/Sidebar/Sidebar";
import DuplicateInstance from "./components/pages/DuplicateInstance/DuplicateInstance";
import { initializeAppAction } from "./sagas/initializeApp/action";
import { resetLoginError } from "./state/login/loginSlice";
import { selectLogin } from "./state/login/loginSlice";
import { selectIsDuplicate } from "./state/singleInstance/singleInstanceSlice";

const LoginPage = React.lazy(() => import("./components/pages/Login/LoginPage"));
const Phone = React.lazy(() => import("./components/pages/Phone/Phone"));
const CallHistory = React.lazy(() => import('./components/pages/CallHistory/CallHistory'));
const Contact = React.lazy(() => import('./components/pages/Contact/Contact'));
const Message = React.lazy(() => import('./components/pages/Message/Message'));
const Voicemail = React.lazy(() => import('./components/pages/Voicemail/Voicemail'));
const CallPark = React.lazy(() => import('./components/pages/CallPark/CallPark'));
const Setting = React.lazy(() => import('./components/pages/Setting/Setting'))
// const Feedback = React.lazy(() => import('./components/pages/Feedback/Feedback'));
const Analytics = React.lazy(() => import('./components/pages/Analytics/Analytics.jsx'));
const CallCenter = React.lazy(() => import('./components/pages/CallCenter/CallCenter'));

let ErrorBoundary = React.Fragment;
if (process.env.NODE_ENV === "production") {
  ErrorBoundary = bugsnagClient.getPlugin('react').createErrorBoundary(React);
}

const App = () => {
  const dispatch = useDispatch();
  const isLoggedIn = useSelector(selectLogin).isLoggedIn;
  const isDuplicate = useSelector(selectIsDuplicate);

  useEffect(() => {
    // Reset login error when the app loads
    dispatch(resetLoginError());
    if (isLoggedIn) {
      // Re-initialize App
      dispatch(initializeAppAction());
    }
  }, [dispatch]);

  if (isDuplicate) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <DuplicateInstance />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ErrorBoundary>
        <Router >
          <Routes>
            <Route path={routes.getLoginRoute()}
              element={
                isLoggedIn ? <Navigate to={routes.getPhoneRoute()} replace /> : (
                  <Suspense fallback={<Loading />}>
                    <LoginPage />
                  </Suspense>
                )
              }
            />
            <Route element={<Sidebar />}>
              <Route path={routes.getPhoneRoute()} element={<Phone />} />
              <Route path={routes.getCdrRoute()} element={<CallHistory />} />
              <Route path={routes.getContactRoute()} element={<Contact />} />
              <Route path={routes.getMessageRoute()} element={<Message />} />
              <Route path={routes.getVoicemailRoute()} element={<Voicemail />} />
              <Route path={routes.getCallParkRoute()} element={<CallPark />} />
              <Route path={routes.getSettingRoute()} element={<Setting />} />
              {/* <Route path={routes.getFeedbackRoute()} element={<Feedback />} /> */}
              <Route path={routes.getAnalyticsRoute()} element={<Analytics />} />
              <Route path={routes.getCallCenterRoute()} element={<CallCenter />} />
            </Route>
            <Route
              path="*"
              element={
                <Navigate
                  to={isLoggedIn ? routes.getPhoneRoute() : routes.getLoginRoute()}
                  replace
                />
              }
            />
          </Routes>
        </Router>
      </ErrorBoundary>
    </ThemeProvider >
  );
};
export default App;